import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import InfiniteScroll from 'react-infinite-scroller';
import type { Content } from '@jwp/ott-common/types/config';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { useWatchHistoryStore } from '@jwp/ott-common/src/stores/WatchHistoryStore';
import { mediaURL, slugify } from '@jwp/ott-common/src/utils/urlFormatting';
import { parseAspectRatio, parseTilesDelta } from '@jwp/ott-common/src/utils/collection';
import { testId } from '@jwp/ott-common/src/utils/common';
import { PLAYLIST_LIMIT, PersonalShelf } from '@jwp/ott-common/src/constants';
import usePlaylists, { type UsePlaylistResult } from '@jwp/ott-hooks-react/src/usePlaylists';
import ApiService from '@jwp/ott-common/src/services/ApiService';
import { getModule } from '@jwp/ott-common/src/modules/container';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';

import Shelf from '../../components/Shelf/Shelf';
import InfiniteScrollLoader from '../../components/InfiniteScrollLoader/InfiniteScrollLoader';
import ErrorPage from '../../components/ErrorPage/ErrorPage';
import CardGrid from '../../components/CardGrid/CardGrid';
import Button from '../../components/Button/Button';

import styles from './ShelfList.module.scss';

import { getLibraryById } from '@jwp/ott-common/src/controllers/graphql';
import useMedia from '@jwp/ott-hooks-react/src/useMedia';

const INITIAL_ROWS_TO_LOAD = 6;
const ROWS_TO_LOAD_STEP = 4;

type Props = {
  rows: Content[];
  /** When true and exactly one shelf row is configured, render a grid + paginated "Load more" (hub single-playlist). */
  isHubContent?: boolean;
};

const ShelfList = ({ rows, isHubContent = false }: Props) => {
  const { accessModel } = useConfigStore(({ accessModel }) => ({ accessModel }), shallow);
  const [rowsToLoad, setRowsToLoad] = useState(INITIAL_ROWS_TO_LOAD);
  const [updatedPlaylist, setUpdatedPlaylist] = useState<UsePlaylistResult>([]);
  const [showLoadMore, setShowLoadMore] = useState(true);
  const { t } = useTranslation('error');
  const { t: tVideo } = useTranslation('video');
  const breakpoint = useBreakpoint();

  const watchHistoryDictionary = useWatchHistoryStore((state) => state.getDictionaryWithSeries());

  const { user, subscription } = useAccountStore(({ user, subscription }) => ({ user, subscription }), shallow);

  const isSinglePlaylist = Boolean(isHubContent && rows.length === 1);

  const playlists = usePlaylists(rows, rowsToLoad);
  const apiService = getModule(ApiService);

  const { SelectedLibraryData: { libraryList = [] } = {} } = JSON.parse(localStorage.getItem('SelectedLibraryData') || '{}');

  const libraryParentId = libraryList?.[0]?.libraryParent?.libraryId ?? null;

  const [libraryPlaylistId, setLibraryPlaylistId] = useState<string | null>(null);

  useEffect(() => {
    if (!libraryParentId) return;

    let cancelled = false;

    const fetchLibraryHub = async () => {
      try {
        const data = await getLibraryById(libraryParentId);
        const hubId = data?.GetLibraryById?.libraryHub?.[0]?.mediaHubId;

        if (!cancelled && hubId) {
          setLibraryPlaylistId(hubId);
        }
      } catch (err) {
        console.error('Failed to fetch library hub', err);
      }
    };

    fetchLibraryHub();

    return () => {
      cancelled = true;
    };
  }, [libraryParentId]);

  const { data: hubData } = useMedia(libraryPlaylistId ?? undefined);

  useEffect(() => {
    return () => {
      setRowsToLoad(INITIAL_ROWS_TO_LOAD);
      setUpdatedPlaylist([]);
      setShowLoadMore(true);
    };
  }, [rows]);

  useEffect(() => {
    if (!isSinglePlaylist) return;
    const first = (updatedPlaylist.length ? updatedPlaylist[0] : playlists[0])?.data;
    if (!first?.playlist?.length) return;
    if (first.playlist.length < PLAYLIST_LIMIT) {
      setShowLoadMore(false);
    }
  }, [isSinglePlaylist, playlists, updatedPlaylist]);

  const handleLastItemClick = useCallback(
    async (idx: number) => {
      const playListLength =
        (updatedPlaylist.length ? updatedPlaylist[idx].data?.playlist?.length : playlists[idx].data?.playlist?.length) ?? 0;

      if (playListLength % PLAYLIST_LIMIT !== 0) {
        if (isSinglePlaylist) setShowLoadMore(false);
        return;
      }

      const currentData = (updatedPlaylist.length ? updatedPlaylist[idx].data : playlists[idx].data) ?? playlists[idx].data;
      const feedid = currentData?.feedid;
      if (!feedid) return;

      const fetchedPlaylist = await apiService.getPlaylistById(feedid, {
        page_limit: PLAYLIST_LIMIT.toString(),
        page_offset: (playListLength + 1).toString(),
      });

      if (!fetchedPlaylist) return;

      const baseList = updatedPlaylist.length > 0 ? updatedPlaylist : playlists;

      const combinedPlayList = updatedPlaylist.length
        ? updatedPlaylist[idx].data?.playlist.concat(fetchedPlaylist.playlist)
        : playlists[idx].data?.playlist.concat(fetchedPlaylist.playlist);

      if (!combinedPlayList) return;

      const res = baseList.toSpliced(idx, 1, {
        ...baseList[idx],
        data: {
          ...baseList[idx].data,
          title: baseList[idx].data?.title ?? '',
          playlist: combinedPlayList,
          links: fetchedPlaylist.links,
        },
      }) as UsePlaylistResult;
      setUpdatedPlaylist(res);
      setShowLoadMore(!!fetchedPlaylist.links?.next);
    },
    [apiService, isSinglePlaylist, playlists, updatedPlaylist],
  );

  const allPlaylistsEmpty = updatedPlaylist.length
    ? updatedPlaylist.every(({ data, isSuccess }) => isSuccess && !data?.playlist?.length)
    : playlists.every(({ data, isSuccess }) => isSuccess && !data?.playlist?.length);

  if (allPlaylistsEmpty) {
    return <ErrorPage title={t('empty_shelves_heading')} message={t('empty_shelves_description')} />;
  }

  if (isSinglePlaylist) {
    const row = rows[0];
    const { data: playlistRaw, isLoading } = updatedPlaylist.length ? updatedPlaylist[0] : playlists[0];

    let singleBody: React.ReactNode;
    if (playlistRaw?.playlist?.length) {
      const playlistForGrid =
        row.featured && hubData ? { ...playlistRaw, playlist: [hubData, ...playlistRaw.playlist] } : playlistRaw;
      const playlistData = playlistForGrid;
      const gridLoading = updatedPlaylist.length ? updatedPlaylist[0].isLoading : playlists[0].isLoading;
      const shelfTitle = row.title ?? playlistData.title;
      const shelfSlug = row.type === 'playlist' ? slugify(row.title || playlistData.title) : row.type;

      singleBody = (
        <section
          key={`single_${playlistData.feedid ?? playlistData.title}`}
          className={classNames(styles.shelfContainer, styles.grid)}
          data-testid={testId(`shelf-${shelfSlug}`)}
          aria-label={shelfTitle}
        >
          <h2 className={styles.title}>{playlistData.title}</h2>
          <CardGrid
            getUrl={(item) => mediaURL({ media: item, playlistId: playlistData.feedid })}
            playlist={playlistData}
            accessModel={accessModel}
            isLoading={gridLoading || false}
            isLoggedIn={!!user}
            hasSubscription={!!subscription}
            headingLevel={3}
          />
          {showLoadMore && !!playlistData.links?.next && (
            <div className={styles.loadMoreBtn}>
              <Button
                label={tVideo('Load_more')}
                aria-label={tVideo('loadMore')}
                onClick={() => void handleLastItemClick(0)}
                color="default"
                fullWidth={breakpoint < Breakpoint.md}
                size="large"
                type="button"
              />
            </div>
          )}
        </section>
      );
    } else {
      singleBody = isLoading ? <InfiniteScrollLoader key="single-hub-loader" /> : null;
    }

    return <div className={styles.shelfList}>{singleBody}</div>;
  }

  return (
    <div className={styles.shelfList}>
      <InfiniteScroll
        style={{ overflow: 'hidden' }}
        loadMore={() => setRowsToLoad((current) => current + ROWS_TO_LOAD_STEP)}
        hasMore={rowsToLoad < rows.length}
        loader={<InfiniteScrollLoader key="loader" />}
      >
        {rows.slice(0, rowsToLoad).map(({ type, featured, title }, index) => {
          const { data: playlist, isLoading, error } = updatedPlaylist.length ? updatedPlaylist[index] : playlists[index];

          if (!playlist?.playlist?.length) return null;

          const posterAspect = parseAspectRatio(playlist.cardImageAspectRatio || playlist.shelfImageAspectRatio);
          const visibleTilesDelta = parseTilesDelta(posterAspect);

          const featuredPlaylist = featured && hubData ? { ...playlist, playlist: [hubData, ...playlist.playlist] } : playlist;

          let shelfTestIdSuffix: string;
          if (featured) {
            shelfTestIdSuffix = 'featured';
          } else if (type === 'playlist') {
            shelfTestIdSuffix = slugify(title || playlist.title);
          } else {
            shelfTestIdSuffix = type;
          }

          return (
            <section
              key={`${index}_${playlist.id}`}
              className={classNames(styles.shelfContainer, { [styles.featured]: featured })}
              data-testid={testId(`shelf-${shelfTestIdSuffix}`)}
              aria-label={title || playlist.title}
            >
              <Shelf
                loading={isLoading}
                error={error}
                type={type}
                playlist={featuredPlaylist}
                watchHistory={type === PersonalShelf.ContinueWatching ? watchHistoryDictionary : undefined}
                title={title || playlist.title}
                featured={featured}
                accessModel={accessModel}
                isLoggedIn={!!user}
                hasSubscription={!!subscription}
                posterAspect={posterAspect}
                visibleTilesDelta={visibleTilesDelta}
                onLastItemClick={() => void handleLastItemClick(index)}
              />
            </section>
          );
        })}
      </InfiniteScroll>
    </div>
  );
};

export default ShelfList;
